import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShareButton from '../components/ShareButton';
import { getBankIndex, getUpiAppIndex, getCreditCardIndex, getSearchSuggestions } from '../services/dataService';
import { BankIndex, UpiAppIndex, CreditCardIndex, SearchSuggestion } from '../types';
import { useCountry } from '../context/CountryContext';
import FAQItem from '../components/FAQ';

// Helper to get initials
const getInitials = (name: string) => name.charAt(0).toUpperCase();

// Bank Card (Horizontal Scroll Style)
const BankCard: React.FC<{ title: string; link: string }> = ({ title, link }) => (
    <Link to={link} className="flex-shrink-0 w-36 sm:w-40 p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center text-center group snap-center">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-13 9 13m-9 3H3m15 0a3 3 0 10-6 0 3 3 0 006 0z"></path></svg>
        </div>
        <p className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2 group-hover:text-blue-600 transition-colors">{title}</p>
    </Link>
);

// List Row Card (For UPI & Cards)
const ListRowCard: React.FC<{ title: string; link: string; type: 'UPI' | 'CARD' }> = ({ title, link, type }) => {
    const isUpi = type === 'UPI';
    return (
        <Link to={link} className="flex items-center p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 group">
            {/* Icon */}
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg mr-3 sm:mr-4 flex-shrink-0 ${isUpi ? 'bg-emerald-500' : 'bg-gradient-to-br from-pink-500 to-purple-500'}`}>
                {isUpi ? (
                    getInitials(title)
                ) : (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
                )}
            </div>
            
            {/* Text */}
            <div className="flex-grow min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base group-hover:text-blue-600 transition-colors truncate">{title}</h3>
            </div>

            {/* Chevron */}
            <div className="text-gray-300 group-hover:text-blue-500 transition-colors ml-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
            </div>
        </Link>
    );
};

// Levenshtein distance function for fuzzy matching
function calculateLevenshteinDistance(a: string = '', b: string = ''): number {
    const s1 = a || '';
    const s2 = b || '';

    if (s1 === s2) return 0;
    if (s1.length === 0) return s2.length;
    if (s2.length === 0) return s1.length;

    const matrix = Array(s2.length + 1).fill(null).map(() => Array(s1.length + 1).fill(null));

    for (let i = 0; i <= s1.length; i += 1) {
        matrix[0][i] = i;
    }

    for (let j = 0; j <= s2.length; j += 1) {
        matrix[j][0] = j;
    }

    for (let j = 1; j <= s2.length; j += 1) {
        for (let i = 1; i <= s1.length; i += 1) {
            const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
            matrix[j][i] = Math.min(
                matrix[j][i - 1] + 1, // deletion
                matrix[j - 1][i] + 1, // insertion
                matrix[j - 1][i - 1] + indicator, // substitution
            );
        }
    }
    return matrix[s2.length][s1.length];
}

// Improved Helper to remove stop words and clean query
function cleanQuery(input: string): string {
    const stopWords = new Set([
        'what', 'is', 'are', 'does', 'do', 'mean', 'meaning', 'how', 'why', 
        'the', 'a', 'an', 'in', 'on', 'for', 'to', 'my', 'i', 'can', 'please', 'tell', 'me', 'about', 
        'charge', 'charges', 'fee', 'fees', 'bank', 'card', 'money', 'cost', 'costs',
        'was', 'were', 'am', 'been', 'being', 'named', 'called', 'known', 'as', 'by', 'with', 'from', 'at', 'it', 'this', 'that',
        'why', 'was', 'charged', 'did', 'get', 'help', 'find', 'looking', 'search', 'know', 'regarding', 'payment', 'transaction'
    ]);
    const cleaned = input.replace(/[^a-zA-Z0-9\s]/g, ' '); // Replace special chars with space to preserve separation
    return cleaned.toLowerCase().split(/\s+/).filter(w => !stopWords.has(w) && w.length > 0).join(' ');
}

const getScore = (item: SearchSuggestion, rawQuery: string, cleanedQuery: string): number => {
    if (!rawQuery) return 0;
    
    const qRaw = rawQuery.toLowerCase().trim();
    const qClean = cleanedQuery.toLowerCase().trim();
    
    const itemCode = item.code?.toLowerCase() || '';
    const itemName = item.name.toLowerCase();
    const itemDetails = item.details?.toLowerCase() || '';
    
    // Boost for natural language questions about charges
    const isQuestion = /what|how|why|mean|tell/i.test(rawQuery);
    const hasChargeContext = /charge|fee|cost/i.test(rawQuery);
    let scoreBoost = 0;
    if (item.type === 'Charge' && (isQuestion || hasChargeContext)) {
        scoreBoost = 20; 
    }

    // --- 1. Critical Token Match (The "Silver Bullet") ---
    const queryTokens = qClean.split(/\s+/).filter(t => t.length > 1); // Ignore single chars
    const itemTokens = itemName.split(/\s+/);
    
    let criticalMatch = false;
    
    // Check if ANY query token matches the Item Code exactly
    if (itemCode && queryTokens.includes(itemCode)) {
        criticalMatch = true;
    }
    
    // Check if ANY query token matches a word in the Item Name exactly
    if (!criticalMatch) {
        for (const qToken of queryTokens) {
            if (itemTokens.includes(qToken)) {
                criticalMatch = true;
                break;
            }
        }
    }

    if (criticalMatch) {
        return 300 + scoreBoost; // Massive score for explicit token match
    }

    // --- 2. Exact Full Match ---
    if (itemCode === qClean || itemCode === qRaw) return 200 + scoreBoost;
    if (itemName === qClean || itemName === qRaw) return 190 + scoreBoost;

    // --- 3. Token Intersection (Partial Matches) ---
    // Check intersection of tokens
    let matches = 0;
    const allItemTokens = itemTokens.concat(itemCode ? [itemCode] : []);
    
    for (const qToken of queryTokens) {
        // Startswith word match (e.g. "hdf" -> "hdfc")
        if (allItemTokens.some(it => it.startsWith(qToken))) {
            matches += 1;
        }
    }

    if (matches > 0) {
        // High score for matching all tokens in a multi-token query
        if (matches === queryTokens.length) return 150 + scoreBoost;
        // Moderate score for partial token matches
        return 100 + (matches * 10) + scoreBoost;
    }

    // --- 4. Starts With ---
    if (itemCode.startsWith(qClean)) return 90 + scoreBoost;
    if (itemName.startsWith(qClean)) return 80 + scoreBoost;
    
    // --- 5. Contains ---
    if (itemCode && itemCode.length >= 2 && qRaw.includes(itemCode)) return 70 + scoreBoost;
    if (itemName.length >= 3 && itemName.includes(qClean)) return 60 + scoreBoost;
    if (itemDetails && itemDetails.includes(qClean)) return 50 + scoreBoost;

    // --- 6. Fuzzy Match (Levenshtein) - Only if query is substantial ---
    if (qClean.length > 2) {
        const codeDist = itemCode ? calculateLevenshteinDistance(itemCode, qClean) : 100;
        const nameDist = calculateLevenshteinDistance(itemName, qClean);
        
        // Allow 1 mistake for short words, 2 for longer
        const threshold = qClean.length > 5 ? 2 : 1;

        if (itemCode && itemCode.length > 2 && codeDist <= 1) return 45 + scoreBoost;
        if (nameDist <= threshold) return 40 + scoreBoost;
    }

    return 0;
};


const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { country } = useCountry();
    
    const [banks, setBanks] = useState<BankIndex[]>([]);
    const [upiApps, setUpiApps] = useState<UpiAppIndex[]>([]);
    const [cards, setCards] = useState<CreditCardIndex[]>([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [allSearchableItems, setAllSearchableItems] = useState<SearchSuggestion[]>([]);
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);

    const paymentAppPath = country === 'IN' ? '/upi-charges' : '/payment-app-charges';

    // Get dynamic placeholder based on country with shorter text for mobile
    const searchPlaceholder = useMemo(() => {
        const isMobile = window.innerWidth < 640;
        switch (country) {
            case 'IN': return isMobile ? "Search IMPS, SBI, or charges..." : "e.g., IMPS REV, SBI, or 'What is an NFS charge?'";
            case 'US': return isMobile ? "Search OD Fee, Chase..." : "e.g., OD Fee, Chase, or 'What is an NSF fee?'";
            case 'GB': return isMobile ? "Search CHAPS, Barclays..." : "e.g., CHAPS, Barclays, or 'Overdraft fees'";
            case 'CA': return isMobile ? "Search Interac, RBC..." : "e.g., Interac Fee, RBC, or 'NSF charge'";
            case 'AU': return isMobile ? "Search CommBank, Osko..." : "e.g., CommBank, Osko, or 'International fee'";
            case 'SG': return isMobile ? "Search DBS, PayNow..." : "e.g., Fall-below fee, DBS, or 'PayNow charges'";
            default: return "Search charges or banks...";
        }
    }, [country]);

    // Data Fetching Effect
    useEffect(() => {
        setIsDataLoading(true);
        // Using Promise.all to fetch all country-specific data in parallel
        Promise.all([
            getBankIndex(country).then(data => setBanks(data.slice(0, 10))),
            getUpiAppIndex(country).then(data => setUpiApps(data.slice(0, 5))),
            getCreditCardIndex(country).then(data => setCards(data.slice(0, 5))),
            getSearchSuggestions(country).then(data => {
                setAllSearchableItems(data);
                // Clear suggestions momentarily to prevent stale data from showing during transition
                setSuggestions([]); 
            })
        ]).finally(() => {
            setIsDataLoading(false);
        });
    }, [country]);
    
    // Filtering Effect: Re-runs whenever query OR data source changes
    useEffect(() => {
        if (!isDataLoading && searchQuery.length > 1) {
            const rawQuery = searchQuery;
            const cleaned = cleanQuery(rawQuery);
            
            const scoredAndFiltered = allSearchableItems
                .map(item => {
                    const score = getScore(item, rawQuery, cleaned);
                    return { ...item, score };
                })
                .filter(item => item.score > 0)
                .sort((a, b) => b.score - a.score)
                .slice(0, 7); // Limit to top 7

            setSuggestions(scoredAndFiltered);
        } else if (searchQuery.length <= 1) {
            setSuggestions([]);
        }
    }, [searchQuery, allSearchableItems, isDataLoading]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const query = event.target.value;
        setSearchQuery(query);

        if (query.length > 1) {
            setIsSuggestionsVisible(true);
        } else {
            setIsSuggestionsVisible(false);
        }
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const query = searchQuery.trim();
        if (!query) return;

        setIsSuggestionsVisible(false);

        const cleaned = cleanQuery(query);
        // Recalculate scores to find best match right now
        const bestMatch = allSearchableItems
            .map(item => ({ ...item, score: getScore(item, query, cleaned) }))
            .sort((a, b) => b.score - a.score)[0];

        // If we have a very strong match for a Charge, go directly there
        if (bestMatch && bestMatch.score >= 150 && bestMatch.type === 'Charge') {
             navigate(`/charges/${bestMatch.code || bestMatch.name}`); 
             return;
        }
        
        // Otherwise use AI response
        navigate(`/ai-response?q=${encodeURIComponent(query)}`);
    };
    
    const handleSuggestionClick = () => {
        setSearchQuery('');
        setSuggestions([]);
        setIsSuggestionsVisible(false);
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'Bank':
                return 'bg-blue-100 text-blue-800 border border-blue-200';
            case 'UPI':
            case 'Payment App':
                return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
            case 'Card':
                return 'bg-purple-100 text-purple-800 border border-purple-200';
            case 'Charge':
                return 'bg-amber-100 text-amber-800 border border-amber-200';
            default:
                return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    const faqData = [
      {
        question: "What is an 'NFS' charge on my bank statement?",
        answer: "NFS stands for 'National Financial Switch'. It's a charge applied when you use another bank's ATM for non-financial transactions (like balance checks or mini-statements) after exhausting your free limit. To avoid it, use your own bank's ATM or mobile app for these tasks.",
      },
      {
        question: "I see 'REV' next to a transaction. What does it mean?",
        answer: "'REV' is short for 'Reversal'. It indicates that a previous transaction has been cancelled and the money has been credited back to your account. This often happens if a transaction fails due to a technical error or incorrect details.",
      },
      {
        question: "Are all UPI payments in India free?",
        answer: "For most users, yes. Standard bank-to-bank UPI transactions remain free. However, merchants may be charged an interchange fee for payments over ₹2,000 made via a digital wallet, though this cost is not directly passed to the customer.",
      },
      {
        question: "How can I find a specific charge on ChargesDecoder?",
        answer: "Simply type the charge code (e.g., 'IMPS REV') or the name of a bank into the search bar on the homepage. You can also browse our 'All Charges' page or navigate by category like 'Banks' or 'UPI'.",
      },
    ];

    return (
        <div className="bg-background">
            {/* Hero Section */}
            <section className="relative text-white py-8 md:py-32 bg-hero-gradient">
                <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
                    
                    {/* Trusted Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6 sm:mb-8 animate-fade-in-up">
                        <span className="text-yellow-300 text-base sm:text-lg">✨</span>
                        <span className="text-xs sm:text-sm font-medium text-white tracking-wide">Trusted by 10,000+ users</span>
                    </div>

                    <h1 className="text-2xl sm:text-4xl md:text-6xl font-extrabold font-poppins leading-tight mb-4 sm:mb-6 drop-shadow-lg">
                        Understand Every Bank <br className="hidden md:block" />
                        <span className="text-highlight-yellow">Charge</span> — <span className="text-highlight-pink">In Seconds</span>.
                    </h1>
                    <p className="text-sm sm:text-lg md:text-xl mb-8 sm:mb-12 max-w-2xl mx-auto text-blue-50 font-light leading-relaxed px-2">
                        Search any charge code (e.g., "IMPS REV") or ask a question. <br className="hidden md:block" /> No jargon. Just instant clarity.
                    </p>
                    
                    <div 
                        className="max-w-2xl mx-auto relative px-2 sm:px-0"
                        onBlur={(e) => {
                            const currentTarget = e.currentTarget;
                            setTimeout(() => {
                                if (currentTarget && !currentTarget.contains(document.activeElement)) {
                                    setIsSuggestionsVisible(false);
                                }
                            }, 200);
                        }}
                    >
                        <form onSubmit={handleSearchSubmit}>
                            <div className="relative flex items-center bg-white rounded-xl md:rounded-2xl shadow-2xl p-1.5 md:p-2 transition-transform hover:scale-[1.01]">
                                <input
                                    type="search"
                                    name="search"
                                    className="flex-grow min-w-0 bg-transparent border-none focus:ring-0 text-gray-800 placeholder-gray-400 text-sm sm:text-base md:text-lg px-3 py-2.5 md:px-4 md:py-3 outline-none truncate"
                                    placeholder={searchPlaceholder}
                                    required
                                    aria-label="Search for a charge or ask a question"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onFocus={() => { if (suggestions.length > 0) setIsSuggestionsVisible(true); }}
                                    autoComplete="off"
                                />
                                <button type="submit" className="flex-shrink-0 bg-button-gradient text-white font-semibold rounded-lg md:rounded-xl px-4 sm:px-6 py-2.5 md:py-3 hover:opacity-90 transition-opacity flex items-center gap-2 shadow-md text-sm md:text-base">
                                    <span className="hidden sm:inline">Decode</span>
                                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                                </button>
                            </div>
                        </form>
                        {isSuggestionsVisible && suggestions.length > 0 && (
                            <ul className="absolute z-20 w-full mt-3 bg-white rounded-xl shadow-2xl text-left overflow-hidden divide-y divide-gray-50 border border-gray-100">
                                {suggestions.map((item, index) => (
                                    <li key={`${item.path}-${index}`}>
                                        <Link 
                                            to={item.path}
                                            onClick={handleSuggestionClick}
                                            className="flex items-center justify-between w-full px-6 py-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors group"
                                        >
                                            <div>
                                                <span className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-base">{item.name}</span>
                                                {item.details && <span className="text-gray-400 ml-2 text-xs uppercase tracking-wide font-medium">{item.details}</span>}
                                            </div>
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded border ${getTypeColor(item.type)}`}>
                                                {item.type.toUpperCase()}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </section>

            {/* Content Sections */}
            <section className="py-8 sm:py-16 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                        
                        {/* Main Content Column */}
                        <div className="lg:col-span-8 flex flex-col gap-8 sm:gap-12">
                            
                            {/* Popular Banks - Horizontal Scroll */}
                            {banks.length > 0 && (
                                <div>
                                    <div className="flex justify-between items-end mb-4 sm:mb-6">
                                        <h2 className="text-xl sm:text-2xl font-bold font-poppins text-gray-900">Popular Banks</h2>
                                        <Link to="/bank-charges" className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors">View all &rarr;</Link>
                                    </div>
                                    <div className="flex overflow-x-auto pb-4 gap-3 sm:gap-4 no-scrollbar snap-x px-1">
                                        {banks.map(bank => (
                                            <BankCard key={bank.id} title={bank.name} link={`/bank-charges/${bank.slug}`} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Popular UPI Apps - Vertical List */}
                            {upiApps.length > 0 && (
                                <div>
                                    <div className="flex justify-between items-end mb-4 sm:mb-6">
                                        <h2 className="text-xl sm:text-2xl font-bold font-poppins text-gray-900">
                                            {country === 'IN' ? 'UPI Apps' : 'Payment Apps'}
                                        </h2>
                                        <Link to={paymentAppPath} className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors">Compare &rarr;</Link>
                                    </div>
                                    <div className="flex flex-col gap-2 sm:gap-3">
                                        {upiApps.map(app => (
                                            <ListRowCard key={app.id} title={app.name} link={`${paymentAppPath}/${app.slug}`} type="UPI" />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Popular Credit Cards - Vertical List */}
                            {cards.length > 0 && (
                                <div>
                                    <div className="flex justify-between items-end mb-4 sm:mb-6">
                                        <h2 className="text-xl sm:text-2xl font-bold font-poppins text-gray-900">Popular Cards</h2>
                                        <Link to="/credit-card-charges" className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors">All Cards &rarr;</Link>
                                    </div>
                                    <div className="flex flex-col gap-2 sm:gap-3">
                                        {cards.map(card => (
                                            <ListRowCard key={card.id} title={card.name} link={`/credit-card-charges/${card.slug}`} type="CARD" />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar Column */}
                        <div className="lg:col-span-4 flex flex-col gap-6 sm:gap-8">
                            {/* Share Section */}
                            <div className="bg-share-gradient rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8 text-center text-white relative overflow-hidden">
                                <div className="relative z-10">
                                     <h2 className="text-xl sm:text-2xl font-bold font-poppins mb-2 sm:mb-3">Help Your Friends</h2>
                                     <p className="text-white/90 mb-4 sm:mb-6 text-sm">Did you find a hidden charge? Share ChargesDecoder so others can avoid it too.</p>
                                     <ShareButton
                                        title="ChargesDecoder - Decode All Bank, UPI & Card Charges Instantly"
                                        text="I found this awesome site that helps decode and understand all Indian bank, UPI, and credit card charges. Check it out!"
                                        className="inline-block w-full"
                                        buttonText="Share Now"
                                        customClasses="bg-white text-blue-600 hover:bg-gray-50 border-transparent shadow-lg px-6 py-3 rounded-full font-bold w-full flex justify-center items-center gap-2"
                                     />
                                </div>
                                <div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
                                <div className="absolute bottom-0 right-0 w-40 h-40 bg-purple-500/20 rounded-full blur-2xl translate-x-1/2 translate-y-1/2 pointer-events-none"></div>
                            </div>

                            {/* FAQ Mini Section */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
                                <div className="flex justify-between items-center mb-4 sm:mb-6">
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Common Questions</h3>
                                </div>
                                <div className="space-y-4">
                                    {faqData.slice(0, 3).map((faq, index) => (
                                        <div key={index} className="pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                                            <p className="font-semibold text-gray-800 text-sm mb-1">{faq.question}</p>
                                            <p className="text-xs text-gray-500 line-clamp-2">{faq.answer}</p>
                                        </div>
                                    ))}
                                    <Link to="/faq" className="block text-center text-sm font-semibold text-blue-600 mt-4 hover:underline">View all FAQs</Link>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;